/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */

import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { createCheckoutSession, CheckoutSessionRequest } from '../../api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Check, Loader2, Sparkles } from 'lucide-react';

interface PricingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type BillingCycle = 'monthly' | 'yearly';

interface PlanFeature {
  text: string;
  included: boolean;
}

interface Plan {
  id: CheckoutSessionRequest['plan'];
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  description: string;
  features: PlanFeature[];
  popular?: boolean;
  icon: React.ReactNode;
}

const plans: Plan[] = [
  {
    id: 'pro_monthly',
    name: 'Pro',
    monthlyPrice: 9.99,
    yearlyPrice: 99,
    description: 'For serious guitarists ready to master the fretboard',
    icon: <Sparkles className="h-5 w-5" />,
    popular: true,
    features: [
      { text: 'All keys and scales unlocked', included: true },
      { text: 'Unlimited Harmonic Companion chats', included: true },
      { text: 'Jam Tracks', included: true },
      { text: 'Advanced practice modes', included: true },
      { text: 'Progress tracking', included: true },
      { text: 'Custom tunings', included: true },
    ],
  },
];

export const PricingModal = ({ open, onOpenChange }: PricingModalProps) => {
  const { getAuthToken, isSignedIn, subscriptionTier } = useAuth();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleUpgrade = async (plan: Plan) => {
    if (!isSignedIn) {
      setError('Please sign in to upgrade your subscription');
      return;
    }

    setLoading(plan.name);
    setError(null);

    try {
      const token = await getAuthToken();
      if (!token) {
        setError('Authentication failed. Please sign in again.');
        return;
      }

      // Determine plan ID based on billing cycle
      const planId = billingCycle === 'yearly'
        ? (plan.id?.replace('_monthly', '_yearly') as CheckoutSessionRequest['plan'])
        : plan.id;

      const response = await createCheckoutSession(
        {
          plan: planId,
          successUrl: `${window.location.origin}/subscription/success`,
          cancelUrl: `${window.location.origin}/subscription/cancel`,
        },
        token
      );

      // Redirect to Stripe Checkout
      window.location.href = response.url;
    } catch (err) {
      console.error('Failed to create checkout session:', err);
      setError('Failed to start checkout. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const getPrice = (plan: Plan) => {
    if (billingCycle === 'yearly') {
      return plan.yearlyPrice;
    }
    return plan.monthlyPrice;
  };

  const getSavings = (plan: Plan) => {
    const monthlyTotal = plan.monthlyPrice * 12;
    const yearlyTotal = plan.yearlyPrice;
    const savings = Math.round(((monthlyTotal - yearlyTotal) / monthlyTotal) * 100);
    return savings;
  };

  const isCurrentPlan = (plan: Plan) => {
    const planTier = plan.name.toLowerCase();
    return subscriptionTier === planTier;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            Upgrade Your Experience
          </DialogTitle>
          <DialogDescription className="text-center">
            Unlock all keys, scales, and unlimited Harmonic Companion access
          </DialogDescription>
        </DialogHeader>

        {/* Billing Toggle */}
        <div className="flex justify-center my-4">
          <div className="inline-flex items-center bg-muted rounded-lg p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'yearly'
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Yearly
              <span className="ml-1.5 text-xs text-green-600 font-semibold">Save 17%</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="text-red-600 text-sm text-center mb-4 p-2 bg-red-50 rounded">
            {error}
          </div>
        )}

        {/* Pricing Card */}
        <div className="flex justify-center mt-4">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-xl border-2 p-6 ${
                plan.popular
                  ? 'border-indigo-500 shadow-lg'
                  : 'border-border'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-indigo-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="flex items-center gap-2 mb-2">
                <div className={`p-2 rounded-lg ${plan.popular ? 'bg-indigo-100 text-indigo-600' : 'bg-purple-100 text-purple-600'}`}>
                  {plan.icon}
                </div>
                <h3 className="text-xl font-bold">{plan.name}</h3>
              </div>

              <p className="text-muted-foreground text-sm mb-4">
                {plan.description}
              </p>

              <div className="mb-4">
                <span className="text-4xl font-bold">${getPrice(plan)}</span>
                <span className="text-muted-foreground">
                  /{billingCycle === 'yearly' ? 'year' : 'month'}
                </span>
                {billingCycle === 'yearly' && (
                  <div className="text-sm text-green-600 font-medium">
                    Save {getSavings(plan)}% vs monthly
                  </div>
                )}
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{feature.text}</span>
                  </li>
                ))}
              </ul>

              <Button
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                onClick={() => handleUpgrade(plan)}
                disabled={loading !== null || isCurrentPlan(plan)}
              >
                {loading === plan.name ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : isCurrentPlan(plan) ? (
                  'Current Plan'
                ) : (
                  `Upgrade to ${plan.name}`
                )}
              </Button>
            </div>
          ))}
        </div>

        <p className="text-xs text-center text-muted-foreground mt-4">
          Secure payment powered by Stripe. Cancel anytime.
        </p>
      </DialogContent>
    </Dialog>
  );
};
